<template>
    <div class="questionnaire-wrapper bg-white p-4 sm:p-6 md:px-10 flex flex-col">
        <loader v-if="loading" class="m-auto"/>
        <div class="flex flex-col flex-1" v-else>
            <div class="progress relative w-full bg-lightGreen-200 rounded-full text-center text-muted-500 text-base md:text-2xl py-1 sm:py-2 md:py-3 pl-6 mb-8 md:mb-12 self-start overflow-hidden">
            <span :style="styles"
                  :class="{'rounded-full' : styles.width === '100%' }"
                  class="h-full absolute inset-0 bg-primary-100"></span>
                <div class="relative z-10" v-html="progress()"></div>
            </div>

            <div class="flex flex-col flex-1">
                <component
                        v-if="question"
                        :is="`question-${question.type}`"
                        :key="question.id"
                        :answer="answer"
                        v-bind="question"
                        ref="questionComponent"
                        @input="answer = $event"
                />
            </div>

            <div class="w-full flex items-center justify-between mt-6 md:mt-12 self-end">
                <button type="button" @click="skip"
                        :disabled="required"
                        :class="{'hover:bg-lightGrey-200 text-muted-500':!required, 'text-muted-100 cursor-not-allowed':required}"
                        class="text-base md:text-2xl lg:text-3xl bg-lightGrey-100 font-bold py-1 sm:py-2 px-10 sm:px-12 md:px-16 lg:px-20 rounded-full">Skip</button>
                <button type="button" @click="next"
                        class="text-base md:text-2xl lg:text-3xl bg-lightGreen-100 hover:bg-lightGreen-200 text-lightGreen-300 font-bold py-1 sm:py-2 px-10 sm:px-12 md:px-16 lg:px-20 rounded-full">Next</button>
            </div>
        </div>
    </div>
</template>

<script>
    import { firebase, db } from '../../firebase';
    import Questionnaire from '../../data/questionnaire';
    import QuestionsFlow from '../../questions/questions-flow'
    import QuestionsScore from '../../questions/questions-score';
    import QuestionsScoreStage from '../../questions/questions-score-stage';
    import Loader from '../shared/Loader';

    export default {
        components: { Loader },

        data() {
            const questionnaireStorage = new Questionnaire(this);
            const questionnaire = questionnaireStorage.get() || questionnaireStorage.set('stage');

            return {
                loading: true,
                userId: null,
                questionnaireStorage,
                questionnaire,
                question: null,
                questions: [],
                answer: [],
                skipping: false,
                shareToken: null,
                flow: new QuestionsFlow(),
                score: null,
            }
        },

        watch: {
            '$route.query.qid': {
                handler(questionId) {
                    if (questionId) {
                        this.question = this.flow.setQuestionById(questionId);
                        this.answer = this.flow.getAnswer(this.question);
                    }
                }
            },

            questionnaire: {
                immediate: true,
                handler(questionnaire) {
                    if (!questionnaire) {
                        if (this.flow) {
                            this.flow.reset();
                        }
                        this.question = null;
                        return;
                    }

                    this.questionnaireStorage.set(questionnaire);

                    const questions = db.collection(`questions-${questionnaire}`).orderBy('order', 'asc');

                    this.$bind('questions', questions).then(() => {
                        this.flow.setQuestions(this.questions);
                        const questionId = this.$route.query.qid;
                        if (!questionId) {
                            this.goToQuestion(this.flow.getQuestion().id);
                        } else {
                            this.question = this.flow.setQuestionById(questionId);

                            if (this.question.id !== questionId) {
                                this.goToQuestion(this.question.id);
                            }

                            this.answer = this.flow.getAnswer(this.question);
                        }
                        this.loading = false;
                    });
                },
            },
        },

        methods: {
            next() {
                if (!this.validate()) {
                    return;
                }

                if (!this.skipping) {
                    this.saveAnswer();
                }

                const nextQuestion = this.flow.getNextQuestion();
                if (nextQuestion) {
                    return this.goToQuestion(nextQuestion.id);
                }

                this.loading = true;
                const questionnaire = this.questionnaire;
                this.questionnaire = null;

                if (questionnaire === 'stage') {
                    window.location.href = 'questionnaire-selection';
                } else {
                    window.location.href = `scores?token=${this.shareToken}&q=${questionnaire}&s=${this.$route.query.s}`;
                }
            },

            skip() {
                this.skipping = true;
                this.next();
                this.skipping = false;
            },

            validate() {
                return this.$refs.questionComponent.validate();
            },

            saveAnswer() {
                this.flow.setAnswer(this.answer);
                this.score.setAnswer(this.question, this.answer);

                const userId = this.userId;
                const questionnaire = this.questionnaire;
                const questionId = this.question.id;
                const answers = this.answer;
                const score = this.score.getScore();
                const feedback = this.score.getFeedback();
                const session = this.$route.query.s;

                const data = {
                    [questionnaire]: {
                        [session]: {
                            [questionId]: {
                                answers
                            }
                        }
                    }
                };

                if (score) {
                    data[questionnaire][session][questionId].score = score;
                }

                if (feedback) {
                    data[questionnaire][session][questionId].feedback = feedback;
                }

                if (questionId === 'name') {
                    db.collection('answers').doc(userId).set({meta: {name: answers[0].value + ' ' + answers[1].value}}, {merge: true});
                }

                if (questionId === 'country') {
                    db.collection('answers').doc(userId).set({meta: {country: answers[0].value}}, {merge: true});
                }

                db.collection('answers').doc(userId).set(data, {merge: true});
            },

            goToQuestion(id) {
                let session = this.$route.query.s;

                if (!session) {
                    session = Math.round((new Date()).getTime() / 1000);
                }

                this.$router.replace({query: {
                    ...this.$route.query,
                    qid: id, s: session
                }});
            },

            progress(percent = false) {
                const step = this.questions.findIndex(question => {
                    if (this.question) {
                        return question.id === this.question.id
                    }
                }) + 1;

                const total = this.questions.length;
                const percentNumber = Math.round(step * 100 / total) || 0;
                const stepClass = percentNumber >= 50 ? 'text-white' : 'text-primary-200';
                return percent ? percentNumber : `<span class="${stepClass} font-bold">${step}</span> / ${total}`;
            }
        },

        computed: {
            styles() {
                return {
                    width: `${this.progress(true)}%`
                }
            },
            required() {
                return this.question ? this.question.required : true;
            }
        },

        created() {
            this.score = this.questionnaire === 'stage' ? new QuestionsScoreStage() : new QuestionsScore();

            firebase.auth().onAuthStateChanged(user => {
                if (user) {
                    this.userId = user.uid;

                    const getShareToken = setInterval(() => {
                        db.collection('answers').doc(user.uid).get().then(doc => {
                            const meta = doc.data().meta;

                            if (meta.shareToken) {
                                this.shareToken = meta.shareToken;
                                clearInterval(getShareToken);
                            }
                        });
                    }, 10000);
                } else {
                    window.location.href = 'authenticate';
                }
            });
        },
    };
</script>
